import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useAllBatches, useCreateBatch, useUpdateBatch, useActivateBatch, useDeleteBatch } from '@/hooks/useBatches'
import { useAuth } from '@/hooks/useAuth'
import { BatchForm, type BatchFormData } from '@/components/batches/BatchForm'

type BatchStatusFilter = 'all' | 'draft' | 'active' | 'closed' | 'cancelled'

export const AdminBatchesPage: React.FC = () => {
    const navigate = useNavigate()
    const { isSuperAdmin } = useAuth()
    const { data: batchesResponse, isLoading } = useAllBatches({})
    const { mutate: createBatch, isPending: isCreating } = useCreateBatch()
    const { mutate: updateBatch, isPending: isUpdating } = useUpdateBatch()
    const { mutate: activateBatch } = useActivateBatch()
    const { mutate: deleteBatch } = useDeleteBatch()
    const [statusFilter, setStatusFilter] = useState<BatchStatusFilter>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [showForm, setShowForm] = useState(false)
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
    const [selectedBatch, setSelectedBatch] = useState<any>(null)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)
    const [successMsg, setSuccessMsg] = useState<string | null>(null)

    useEffect(() => {
        if (!errorMsg) return
        const timer = setTimeout(() => setErrorMsg(null), 5000)
        return () => clearTimeout(timer)
    }, [errorMsg])

    useEffect(() => {
        if (!successMsg) return
        const timer = setTimeout(() => setSuccessMsg(null), 5000)
        return () => clearTimeout(timer)
    }, [successMsg])

    const handleCreateBatch = async (data: BatchFormData) => {
        const createPayload = {
            course_id: data.courseId,
            title: data.title,
            description: data.description,
            schedule_type: data.scheduleType || 'weekly',
            start_date: data.startDate ? new Date(data.startDate + 'T00:00:00Z').toISOString() : '',
            end_date: data.endDate ? new Date(data.endDate + 'T23:59:59Z').toISOString() : '',
            registration_deadline: data.registrationDeadline ? new Date(data.registrationDeadline + 'T23:59:59Z').toISOString() : undefined,
            location: data.location || undefined,
            meeting_link: data.meetingLink || undefined,
            capacity: data.capacity,
            minimum_participants: data.minimumParticipants,
            batch_fee: data.batchFee,
            trainer_id: data.trainerId || undefined,
            default_room_capacity: data.defaultRoomCapacity,
            photo_drive_url: data.photoDriveUrl || undefined,
            photo_drive_description: data.photoDriveDescription || undefined,
            sessions: data.sessions.map((s, i) => ({
                session_number: i + 1,
                title: s.title,
                description: s.description || undefined,
                date_time: s.dateTime ? new Date(s.dateTime).toISOString() : '',
                duration_minutes: s.durationMinutes || 120,
                location: s.location || undefined,
                meeting_link: s.meetingLink || undefined,
                type: s.type || 'practical',
            })),
        }

        const updatePayload = {
            title: data.title,
            description: data.description,
            start_date: data.startDate ? new Date(data.startDate + 'T00:00:00Z').toISOString() : '',
            end_date: data.endDate ? new Date(data.endDate + 'T23:59:59Z').toISOString() : '',
            registration_deadline: data.registrationDeadline ? new Date(data.registrationDeadline + 'T23:59:59Z').toISOString() : undefined,
            location: data.location || undefined,
            meeting_link: data.meetingLink || undefined,
            capacity: data.capacity,
            minimum_participants: data.minimumParticipants,
            batch_fee: data.batchFee,
            trainer_id: data.trainerId || undefined,
            default_room_capacity: data.defaultRoomCapacity,
            photo_drive_url: data.photoDriveUrl || undefined,
            photo_drive_description: data.photoDriveDescription || undefined,
        }

        if (selectedBatch) {
            updateBatch({ id: selectedBatch.id, data: updatePayload }, {
                onSuccess: () => {
                    setSuccessMsg('Batch berhasil diperbarui!')
                    setShowForm(false)
                    setSelectedBatch(null)
                },
                onError: (err: any) => {
                    setErrorMsg('Gagal memperbarui batch: ' + (err?.response?.data?.message || err.message))
                }
            })
        } else {
            createBatch(createPayload, {
                onSuccess: () => {
                    setSuccessMsg('Batch berhasil dibuat!')
                    setShowForm(false)
                },
                onError: (err: any) => {
                    setErrorMsg('Gagal membuat batch: ' + (err?.response?.data?.message || err.message))
                }
            })
        }
    }

    const batches = batchesResponse?.data || []

    const filteredBatches = batches.filter((batch: any) => {
        const matchesStatus = statusFilter === 'all' || batch.status === statusFilter
        const matchesSearch = batch.title?.toLowerCase().includes(searchQuery.toLowerCase())
        return matchesStatus && matchesSearch
    })

    const getStatusBadge = (status: string) => {
        const styles: Record<string, string> = {
            active: 'bg-emerald-500 text-white',
            draft: 'bg-amber-500 text-white',
            closed: 'bg-slate-400 text-white',
            cancelled: 'bg-rose-500 text-white'
        }
        const config = styles[status] || styles.draft
        return (
            <div className={`px-2.5 py-1 rounded-full text-xs font-bold ${config}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </div>
        )
    }

    return (
        <div className="space-y-10 pb-20 relative">
            <PageHeader
                title="Gelombang Pelatihan"
                subtitle="Pantau dan kelola jadwal pelatihan yang sedang berlangsung"
                actions={
                    <button
                        onClick={() => setShowForm(true)}
                        className="flex items-center gap-2 px-6 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-[1.5rem] font-bold text-sm hover:opacity-90 active:scale-95 transition-all shadow-xl shadow-slate-200 dark:shadow-none"
                    >
                        <span className="material-symbols-outlined text-[20px]">add_circle</span>
                        Batch Baru
                    </button>
                }
            />

            {/* Inline notifications */}
            {errorMsg && (
                <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center gap-3">
                    <span className="material-symbols-outlined text-[20px]">error</span>
                    <span className="text-sm font-bold flex-1">{errorMsg}</span>
                    <button onClick={() => setErrorMsg(null)} className="material-symbols-outlined text-[18px] hover:opacity-70 transition-opacity">close</button>
                </div>
            )}
            {successMsg && (
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center gap-3">
                    <span className="material-symbols-outlined text-[20px]">check_circle</span>
                    <span className="text-sm font-bold flex-1">{successMsg}</span>
                    <button onClick={() => setSuccessMsg(null)} className="material-symbols-outlined text-[18px] hover:opacity-70 transition-opacity">close</button>
                </div>
            )}

            {/* Filters Section */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 bg-white dark:bg-slate-900/50 p-6 rounded-[2rem] border border-slate-100 dark:border-white/5 shadow-sm">
                <div className="relative flex-1 group">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors">
                        search
                    </span>
                    <input
                        type="text"
                        placeholder="Cari gelombang pelatihan..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent focus:border-primary/30 focus:bg-white rounded-2xl text-sm font-medium outline-none transition-all"
                    />
                </div>

                <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
                    {(['all', 'draft', 'active', 'closed', 'cancelled'] as BatchStatusFilter[]).map((status) => (
                        <button
                            key={status}
                            onClick={() => setStatusFilter(status)}
                            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap border ${statusFilter === status
                                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-transparent shadow-lg'
                                : 'bg-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 border-slate-100 dark:border-white/10'
                                }`}
                        >
                            {status === 'all' ? 'Semua' : status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Batches Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {isLoading ? (
                    <div className="col-span-full text-center py-20 bg-white/50 dark:bg-slate-900/50 rounded-[3rem] border border-slate-100 dark:border-white/5 backdrop-blur-sm">
                        <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-sm font-bold text-slate-500">Menyinkronkan data...</p>
                    </div>
                ) : filteredBatches.length === 0 ? (
                    <div className="col-span-full text-center py-24 bg-white/50 dark:bg-slate-900/50 rounded-[3rem] border border-dashed border-slate-200 dark:border-white/10 backdrop-blur-sm">
                        <div className="size-20 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
                            <span className="material-symbols-outlined text-4xl">inventory_2</span>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Belum Ada Data</h3>
                        <p className="text-sm text-slate-500 font-medium max-w-xs mx-auto">Mulai dengan menambahkan batch pelatihan baru melalui tombol di atas.</p>
                    </div>
                ) : (
                    filteredBatches.map((batch: any) => (
                        <div key={batch.id} className="group bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 p-8 hover:shadow-[0_32px_64px_-12px_rgba(0,0,0,0.08)] transition-all duration-500 flex flex-col relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 transition-all group-hover:scale-150" />
                            
                            <div className="flex items-start justify-between mb-6 relative z-20">
                                {getStatusBadge(batch.status)}
                                <div className="relative">
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setActiveDropdown(activeDropdown === batch.id ? null : batch.id)
                                        }}
                                        className={`size-10 rounded-xl flex items-center justify-center transition-all ${activeDropdown === batch.id ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white'}`}
                                    >
                                        <span className="material-symbols-outlined text-[22px]">more_horiz</span>
                                    </button>

                                    {activeDropdown === batch.id && (
                                        <>
                                            <div className="fixed inset-0 z-10" onClick={() => setActiveDropdown(null)} />
                                            <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-100 dark:border-white/5 z-20 py-2 animate-in fade-in zoom-in-95 duration-200">
                                                <button 
                                                    onClick={() => {
                                                        setSelectedBatch(batch)
                                                        setShowForm(true)
                                                        setActiveDropdown(null)
                                                    }}
                                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">edit</span>
                                                    Edit Batch
                                                </button>
                                                {batch.status === 'draft' && (
                                                    <button 
                                                        onClick={() => {
                                                            if (confirm('Aktifkan batch ini? Setelah aktif, batch akan bisa menerima pendaftaran.')) {
                                                                activateBatch(batch.id, {
                                                                    onSuccess: () => setSuccessMsg('Batch berhasil diaktifkan!'),
                                                                    onError: (err: any) => setErrorMsg('Gagal mengaktifkan batch: ' + (err?.response?.data?.message || err.message))
                                                                })
                                                                setActiveDropdown(null)
                                                            }
                                                        }}
                                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">rocket_launch</span>
                                                        Aktifkan Batch
                                                    </button>
                                                )}
                                                <div className="h-px bg-slate-100 dark:bg-white/5 my-1" />
                                                <button
                                                    onClick={() => {
                                                        if (confirm(`Hapus batch "${batch.title}"? Tindakan ini tidak bisa dibatalkan.`)) {
                                                            deleteBatch(batch.id, {
                                                                onSuccess: () => {
                                                                    setSuccessMsg('Batch berhasil dihapus!')
                                                                    setActiveDropdown(null)
                                                                },
                                                                onError: (err: any) => setErrorMsg('Gagal menghapus batch: ' + (err?.message || 'Terjadi kesalahan'))
                                                            })
                                                        }
                                                    }}
                                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">delete</span>
                                                    Hapus
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 relative z-10">
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                                    {batch.title}
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 line-clamp-2 font-medium leading-relaxed">
                                    {batch.description || 'Kelola operasional untuk gelombang pelatihan ini.'}
                                </p>

                                {/* Warning badges for active batch with issues */}
                                {batch.status === 'active' && (() => {
                                    const now = new Date()
                                    const isDeadlinePassed = batch.registrationDeadline && new Date(batch.registrationDeadline) < now
                                    const isExpired = batch.endDate && new Date(batch.endDate) < now
                                    const isFull = (batch.enrolledCount || 0) >= (batch.capacity || 0)

                                    if (!isDeadlinePassed && !isExpired && !isFull) return null

                                    return (
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {isExpired && (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20">
                                                    <span className="material-symbols-outlined text-[14px]">event_busy</span>
                                                    Batch Selesai
                                                </span>
                                            )}
                                            {isDeadlinePassed && !isExpired && (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
                                                    <span className="material-symbols-outlined text-[14px]">schedule</span>
                                                    Deadline Terlewat
                                                </span>
                                            )}
                                            {isFull && !isExpired && (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20">
                                                    <span className="material-symbols-outlined text-[14px]">group_off</span>
                                                    Penuh
                                                </span>
                                            )}
                                        </div>
                                    )
                                })()}
                            </div>

                            <div className="grid grid-cols-2 gap-6 mb-8 pt-6 border-t border-slate-50 dark:border-white/5 relative z-10">
                                <div className="space-y-2">
                                    <p className="text-sm font-black text-slate-400">Jadwal Mulai</p>
                                    <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200 font-bold text-sm">
                                        <div className="size-8 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                                            <span className="material-symbols-outlined text-primary text-[18px]">calendar_today</span>
                                        </div>
                                        <span>{batch.startDate ? new Date(batch.startDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD'}</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-sm font-black text-slate-400">Kapasitas Sisa</p>
                                    <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200 font-bold text-sm">
                                        <div className="size-8 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                                            <span className="material-symbols-outlined text-primary text-[18px]">group</span>
                                        </div>
                                        <span>{batch.enrolledCount || 0} / {batch.capacity || 0}</span>
                                    </div>
                                </div>
                            </div>

                             <button
                                onClick={() => navigate(`/admin/batches/${batch.id}/rooms`)}
                                className="relative z-10 w-full flex items-center justify-center gap-3 px-4 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-[1.25rem] font-black text-sm hover:shadow-xl active:scale-[0.98] transition-all tracking-tight"
                            >
                                <span className="material-symbols-outlined text-[18px]">meeting_room</span>
                                Kelola Kamar & Jadwal
                            </button>
                            {!isSuperAdmin() && (
                            <button
                                onClick={() => navigate(`/admin/batches/${batch.id}/attendance-scan`)}
                                className="relative z-10 w-full flex items-center justify-center gap-3 px-4 py-3 bg-primary/10 dark:bg-primary/20 text-primary rounded-[1.25rem] font-bold text-sm hover:bg-primary/20 active:scale-[0.98] transition-all"
                            >
                                <span className="material-symbols-outlined text-[18px]">qr_code_scanner</span>
                                Scan Absensi
                            </button>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] p-6" onClick={() => {
                    setShowForm(false)
                    setSelectedBatch(null)
                }}>
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] relative border border-white/20 flex flex-col max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header - Fixed */}
                        <div className="flex items-center justify-between p-10 pb-6 border-b border-slate-100 dark:border-white/5">
                            <div className="flex items-center gap-4">
                                <div className="size-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                                    <span className="material-symbols-outlined text-primary text-[28px]">{selectedBatch ? 'edit_note' : 'add_task'}</span>
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                                        {selectedBatch ? 'Edit Batch' : 'Batch Baru'}
                                    </h2>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1">
                                        {selectedBatch ? 'Ubah detail gelombang pelatihan' : 'Konfigurasi operasional pelatihan baru'}
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => {
                                setShowForm(false)
                                setSelectedBatch(null)
                            }} className="size-10 rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 flex items-center justify-center transition-all text-slate-400">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        
                        {/* Modal Body - Scrollable */}
                        <div className="flex-1 overflow-y-auto p-10 pt-6 custom-scrollbar">
                            <BatchForm
                                onSubmit={handleCreateBatch}
                                onCancel={() => {
                                    setShowForm(false)
                                    setSelectedBatch(null)
                                }}
                                isLoading={isCreating || isUpdating}
                                initialData={selectedBatch ? {
                                    courseId: selectedBatch.courseId,
                                    title: selectedBatch.title,
                                    description: selectedBatch.description || '',
                                    scheduleType: selectedBatch.scheduleType,
                                    startDate: selectedBatch.startDate ? new Date(selectedBatch.startDate).toISOString().split('T')[0] : '',
                                    endDate: selectedBatch.endDate ? new Date(selectedBatch.endDate).toISOString().split('T')[0] : '',
                                    registrationDeadline: selectedBatch.registrationDeadline ? new Date(selectedBatch.registrationDeadline).toISOString().split('T')[0] : '',
                                    location: selectedBatch.location || '',
                                    meetingLink: selectedBatch.meetingLink || '',
                                    capacity: selectedBatch.capacity,
                                    minimumParticipants: selectedBatch.minimumParticipants || 1,
                                    batchFee: selectedBatch.batchFee || 0,
                                    trainerId: selectedBatch.trainerId || '',
                                    defaultRoomCapacity: selectedBatch.defaultRoomCapacity || 2,
                                    photoDriveUrl: selectedBatch.photoDriveUrl || '',
                                    photoDriveDescription: selectedBatch.photoDriveDescription || '',
                                    sessions: [],
                                } : undefined}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminBatchesPage
